import { CSSProperties, ReactNode, useEffect, useId, useRef, useState } from 'react';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Enter/leave motion length — the hand-off waits this long before unmounting. */
export const QUESTION_SHEET_MOTION_MS = 200;

const KEYFRAMES = `
@keyframes sk-sheet-in{from{transform:translateY(12px);opacity:0}to{transform:translateY(0);opacity:1}}
@keyframes sk-sheet-out{from{transform:translateY(0);opacity:1}to{transform:translateY(12px);opacity:0}}
@keyframes sk-sheet-back{from{transform:translateX(-16px);opacity:0}to{transform:translateX(0);opacity:1}}
.sk-sheet-back-btn{transition:transform .12s ease-out,background-color .15s}
.sk-sheet-back-btn:active{transform:scale(.92)}
@media (prefers-reduced-motion:reduce){.sk-sheet-back-btn{transition:none}.sk-sheet-back-btn:active{transform:none}}
`;

/** How the sheet arrives: rising from below (forward), or the question sliding in from the left (back). */
export type QuestionSheetEnter = 'up' | 'back';

/**
 * QuestionSheet — the bottom sheet that replaces the chat composer while Alma
 * asks a structured question. The serif title IS the question (Alma's bubble
 * only bridges); the helper line says how to answer.
 *
 * Title and Continue stay put; only the body scrolls once the sheet reaches
 * ~60% of the space above the on-screen keyboard (long city or specialty
 * lists, or the "Other" field with the keyboard up).
 *
 * `onBack` adds a round back arrow beside the title that reopens the previous
 * question. Omit it on the first question.
 *
 * Focus moves to the title on mount and whenever `title` changes, so screen
 * readers hear each new question. Pass `leaving` to play the exit motion
 * before unmounting (see QUESTION_SHEET_MOTION_MS).
 */
export interface QuestionSheetProps {
  /** The question, in sentence case. */
  title: string;
  /** Muted one-liner on how to answer, e.g. "Pick all that fit." */
  helper?: string;
  /** The inputs (chips, list, date rows, counter). */
  children?: ReactNode;
  /** Muted line under the inputs (e.g. "7 days in Lisbon") or a plain-words validation message. */
  note?: ReactNode;
  /** Shows the Continue button. Omit for single-choice steps that advance on tap. */
  onContinue?: () => void;
  /** Disables Continue until the step is answerable. */
  continueDisabled?: boolean;
  /** Continue label. Default "Continue". */
  continueLabel?: string;
  /** Shows a back arrow that reopens the previous question. */
  onBack?: () => void;
  /** Accessible name of the back arrow. Default "Back to the previous question". */
  backLabel?: string;
  /** Entry motion. 'back' keeps the sheet in place and slides the question in from the left, like an iOS pop. Default 'up'. */
  enter?: QuestionSheetEnter;
  /** A tab bar sits right below the sheet and owns the safe-area gap: keep the bottom padding tight. */
  docked?: boolean;
  /** Plays the leave motion. */
  leaving?: boolean;
  style?: CSSProperties;
}

export function QuestionSheet({
  title,
  helper,
  children,
  note,
  onContinue,
  continueDisabled = false,
  continueLabel = 'Continue',
  onBack,
  backLabel = 'Back to the previous question',
  enter = 'up',
  docked = false,
  leaving = false,
  style,
}: QuestionSheetProps) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const titleId = useId();
  const [reduceMotion] = useState(prefersReducedMotion);

  useEffect(() => {
    titleRef.current?.focus({ preventScroll: true });
  }, [title]);

  const animation = reduceMotion || (enter === 'back' && !leaving)
    ? undefined
    : `${leaving ? 'sk-sheet-out' : 'sk-sheet-in'} ${QUESTION_SHEET_MOTION_MS}ms ease-out both`;
  // Title and inputs slide; the sheet's frame and Continue stay put.
  const slide = reduceMotion || enter !== 'back' || leaving
    ? undefined
    : `sk-sheet-back ${QUESTION_SHEET_MOTION_MS + 20}ms cubic-bezier(0.2, 0.8, 0.2, 1) both`;

  return (
    <section
      aria-labelledby={titleId}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        // Raw dvh ignores the native keyboard, which would push Continue out of view.
        maxHeight: 'calc((100dvh - var(--kb, 0px)) * 0.6)',
        background: 'var(--surface)',
        borderTop: '1px solid var(--line)',
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
        padding: docked ? '18px 16px 12px' : '18px 16px calc(22px + var(--sab))',
        animation,
        ...style,
      }}
    >
      <style>{KEYFRAMES}</style>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 'none', animation: slide }}>
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            aria-label={backLabel}
            className="sk-flow-focus sk-sheet-back-btn sk-hit"
            style={{
              flex: 'none',
              width: 32,
              height: 32,
              // Centers the 32px circle on the title's first line (17px × 1.25).
              marginTop: (17 * 1.25 - 32) / 2,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              border: 'none',
              background: 'var(--chip-bg)',
              color: 'var(--text)',
              cursor: 'pointer',
              // Keeps the touch target at 44px without growing the circle.
              position: 'relative',
            }}
          >
            <span aria-hidden="true" style={{ position: 'absolute', inset: -6 }} />
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M10 3 5 8l5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        ) : null}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          <h2
            id={titleId}
            ref={titleRef}
            tabIndex={-1}
            style={{
              margin: 0,
              fontFamily: 'var(--font-serif)',
              fontSize: 'var(--fs-body)',
              fontWeight: 400,
              letterSpacing: '-0.01em',
              lineHeight: 1.25,
              color: 'var(--text)',
              outline: 'none',
            }}
          >
            {title}
          </h2>
          {helper ? (
            <p style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-caption)', color: 'var(--text-dim)' }}>{helper}</p>
          ) : null}
        </div>
      </div>

      {/* Only this part scrolls, so the question and Continue stay in view. */}
      <div style={{ minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14, animation: slide }}>
        {children}
      </div>

      {note ? (
        <div
          aria-live="polite"
          style={{ flex: 'none', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-caption)', lineHeight: 1.5, color: 'var(--chip-text)' }}
        >
          {note}
        </div>
      ) : null}

      {onContinue ? (
        <button
          type="button"
          onClick={onContinue}
          disabled={continueDisabled}
          className="sk-flow-focus"
          style={{
            flex: 'none',
            height: 44,
            borderRadius: 'var(--r-pill)',
            border: 'none',
            background: 'var(--text)',
            color: 'var(--bg)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-subhead)',
            fontWeight: 600,
            cursor: continueDisabled ? 'not-allowed' : 'pointer',
            // A disabled Continue fades by swapping to the chip tokens, never opacity.
            ...(continueDisabled ? { background: 'var(--chip-bg)', color: 'var(--chip-text)' } : null),
          }}
        >
          {continueLabel}
        </button>
      ) : null}
    </section>
  );
}

export default QuestionSheet;
